import React, { useEffect, useState, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../../hooks/storeHooks';
import {
  updateAssetsDisplayState,
  fetchInitialStateAsync,
  updateSelectedAsset,
  updateSearchGroups,
  Group,
  setSelectedGroupByName,
  setNavigationDrawerCollapsed,
} from '../AssetGroupsSlice';
import { fetchNotificationsAsync } from '../NotificationsSlice';
import { ReactComponent as SearchIcon } from '../../../images/search-md.svg';
import TrackingDecorator from '../../common/Logging/TrackingDecorator';
import { useNavigate, useParams } from 'react-router-dom';
import { setIndustryApplicationId } from '../../asset-analysis/AssetAnalysisSlice';
import NavigationIcons from './NavigationIcons';
import CommStatusIcon from '../../../components/common/CommStatus/CommStatus';
import { ReactComponent as ChevronDown } from '../../../images/chevron-down.svg';
import { fetchDataHistoryTrendLibraryAsync } from './../../data-history/TrendLibrary/TrendLibrarySlice';
import Loader from '../../common/Loader/Loader';
import Tooltip from '../../common/tooltip/ToolTip';
import WellEditModal from '../../../components/well-edit-view/WellEditViewModals';
import { ReactComponent as ChevronIcon } from '../../../images/chevron-left.svg';

export interface TrackingProps {
  onClick: () => void;
  tracking: {
    capture: (eventName: string, eventData: object) => void;
  };
}

const NavigationDrawerItem: React.FC<TrackingProps> = () => {
  const dispatch = useAppDispatch();
  const assetGroups = useAppSelector((state) => state.assetGroups?.searchGroups);
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);
  const selectedGroupAssetId = useAppSelector((state) => state.assetGroups?.selectedGroup?.assetGroupName);
  const assets = useAppSelector((state) => state.assetGroups?.assets);
  const hasWellFilters = useAppSelector((state: any) => state.WellFiltersSlice.hasSelectedFilters);
  const isCollapsed = useAppSelector((state) => state.assetGroups.isNavigationDrawerCollapsed);
  const navigate = useNavigate();
  const [searchString, setSearchString] = useState<string>('');
  const [alreadyOpen, setAlreadyOpen] = useState(false);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [isWellEditModalOpen, setWellEditModalOpen] = useState(false);
  const searchBar = useRef<HTMLInputElement>(null);
  const notificationPromiseRef = useRef<any>(null);
  const scrollTargetRef = useRef<string>('');
  const { groupName } = useParams();
  const { groups } = useAppSelector((state) => state.assetGroups);

  const processedGroupRef = useRef<string>('');

  useEffect(() => {
    if (groupName && assetGroups && assetGroups.length > 0 && assetGroups[0]?.showAssets === false) {
      processedGroupRef.current = '';
    }
  }, [assetGroups, groupName]);

  useEffect(() => {
    if (!groupName || !groups || !assetGroups || assetGroups.length === 0 || groups.length === 0) {
      return;
    }

    const decodedGroupName = decodeURIComponent(groupName);

    // Prevent infinite loop - exit if already processed
    const processKey = `${decodedGroupName}-${groups?.length}-${assetGroups?.length}`;
    if (processedGroupRef.current === processKey) {
      return;
    }

    processedGroupRef.current = processKey;

    const findGroup = (groups: Group[], name: string): Group | null => {
      for (const group of groups) {
        if (group.assetGroupName === name) return group;
        if (group.childGroups) {
          const found = findGroup(group.childGroups, name);
          if (found) return found;
        }
      }
      return null;
    };

    const findParents = (groups: Group[], targetId: number, parents: number[] = []): number[] => {
      for (const group of groups) {
        if (group.id === targetId) return [...parents, targetId];
        if (group.childGroups) {
          const result = findParents(group.childGroups, targetId, [...parents, group.id]);
          if (result.length > 0) return result;
        }
      }
      return [];
    };

    const targetGroup = findGroup(groups, decodedGroupName);

    if (targetGroup) {
      const parentsToExpand = findParents(groups, targetGroup.id);

      // Expand parent groups to show target
      parentsToExpand.forEach((groupId) => {
        dispatch(
          updateAssetsDisplayState({
            groupId,
            assetId: undefined,
            searchString: undefined,
            assetIdGuid: undefined,
            showAssets: true,
          }),
        );
      });

      dispatch(setSelectedGroupByName({ groupName: decodedGroupName }));

      scrollTargetRef.current = decodedGroupName;
    }
  }, [groupName, groups, assetGroups, dispatch]);

  // Auto-scroll to selected group when it becomes active
  useEffect(() => {
    if (!scrollTargetRef.current || selectedGroupAssetId !== scrollTargetRef.current) return;

    requestAnimationFrame(() => {
      const element = document.querySelector(`[data-group-name="${scrollTargetRef.current}"]`) as HTMLElement;
      const scrollContainer = element?.closest('.navigation-content-frame') as HTMLElement;

      if (!element || !scrollContainer) return;

      scrollContainer.scrollTo({
        top: element.offsetTop - scrollContainer.clientHeight / 2,
        behavior: 'smooth',
      });
      scrollTargetRef.current = '';
    });
  }, [selectedGroupAssetId]);

  const handleAssetGroupClick = (id: number) => {
    if (!alreadyOpen) {
      setAlreadyOpen(true);
    }
    dispatch(
      updateAssetsDisplayState({
        groupId: id,
        assetId: undefined,
        searchString: searchString,
        assetIdGuid: undefined,
      }),
    );
  };

  useEffect(() => {
    if (!groupName && assetGroups && assetGroups.length > 0 && !alreadyOpen) {
      const firstGroup = assetGroups[0];
      if (firstGroup && !firstGroup.showAssets) {
        setAlreadyOpen(true);
        dispatch(
          updateAssetsDisplayState({
            groupId: firstGroup.id,
            assetId: undefined,
            searchString: undefined,
            assetIdGuid: undefined,
            showAssets: true,
          }),
        );
      }
    }

    return () => {
      setAlreadyOpen(false);
    };
  }, [groupName, assetGroups, alreadyOpen, dispatch]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (assetGroups && assetGroups?.length > 0) {
        const assetGroup = assetGroups[0];
        if (assetGroup.assets && assetGroup.assets.length > 0) {
          const assetId = assetGroup.assets[0].assetName;
          const assetIdGuid = assetGroup.assets[0].assetId;

          dispatch(
            updateSelectedAsset({
              groupId: assetGroup.id,
              assetId: assetId,
              searchString: searchString,
              assetIdGuid: assetIdGuid,
            }),
          );
        }
      }
    }
  };

  useEffect(() => {
    // tracking.capture('Assets', 'Page Loaded');
    if (searchBar.current) {
      searchBar.current.focus();
      searchBar.current.value = '';
    }
    if (!assetGroups || !assetGroups.length) {
      dispatch(fetchInitialStateAsync());
    }

    setSearchString('');
    setIsSearchTriggered(false);

    if (!searchString) {
      dispatch(
        updateSearchGroups({
          groupId: undefined,
          assetId: undefined,
          searchString: '',
          assetIdGuid: undefined,
        }),
      );
    }
  }, [dispatch]);

  function debounce<T extends (...args: any[]) => void>(func: T, delay: number): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let searchValue = e.target.value;
    let searchData = false;

    if (searchValue.length >= 3) {
      setIsSearchTriggered(true);
      searchData = true;
    }

    if (searchValue.length < 3 && isSearchTriggered) {
      setIsSearchTriggered(false);
      searchData = true;
      searchValue = '';
    }

    if (searchData) {
      setSearchString(searchValue);

      dispatch(
        updateSearchGroups({
          groupId: undefined,
          assetId: undefined,
          searchString: searchValue,
          assetIdGuid: undefined,
        }),
      );
    }
  };

  const throttledHandleSearchInputChange = debounce(handleSearchInputChange, 250);

  const handleAssetIdClick = (
    assetName: string | undefined,
    groupId: number,
    assetId: string,
    industryApplicationId: number,
    groupName: string,
  ) => {
    dispatch(
      updateSelectedAsset({ groupId: groupId, assetId: assetName, searchString: undefined, assetIdGuid: assetId }),
    );

    // Cancel previous notification request if it exists
    if (notificationPromiseRef.current) {
      notificationPromiseRef.current.abort();
    }

    // Create new request and store the promise for cancellation
    notificationPromiseRef.current = dispatch(
      fetchNotificationsAsync({
        type: 'asset',
        id: assetId || '',
        notificationTypeId: '',
      }),
    );

    notificationPromiseRef.current.catch((error: any) => {
      if (error.name !== 'AbortError') {
        console.error('Asset notification request failed:', error);
      }
    });

    dispatch(setIndustryApplicationId({ industryApplicationId: industryApplicationId }));

    if (assetName) {
      let currentTab = location.pathname.split('/')[2];
      currentTab =
        currentTab == undefined || currentTab == 'well-location' || currentTab === 'customViews'
          ? 'status'
          : currentTab;
      if (groupName) {
        const formattedGroupName = groupName.split(' ').join('_');
        navigate(
          `/asset/` +
          currentTab +
          `/${assets?.find((asset) => asset.assetName === assetName)?.assetId}/${encodeURIComponent(
            formattedGroupName,
          )}`,
        );
      }
    }
    dispatch(fetchDataHistoryTrendLibraryAsync({ assetId: assetId, groupName: groupName }));
  };

  const handleAssetGroupNameClick = (groupId: number, groupName: string) => {
    handleAssetGroupClick(groupId);

    dispatch(
      updateSelectedAsset({
        groupId: groupId,
        assetId: undefined,
        searchString: undefined,
        assetIdGuid: undefined,
      }),
    );

    // Cancel previous notification request if it exists
    if (notificationPromiseRef.current) {
      notificationPromiseRef.current.abort();
    }

    // Create new request and store the promise for cancellation
    notificationPromiseRef.current = dispatch(
      fetchNotificationsAsync({ type: 'group', id: encodeURIComponent(groupName) }),
    );

    notificationPromiseRef.current.catch((error: any) => {
      if (error.name !== 'AbortError') {
        console.error('Group notification request failed:', error);
      }
    });

    navigate(`/group/customViews/${encodeURIComponent(groupName ?? '')}`);
  };

  const openAddCustomViewModal = (open: boolean) => {
    setWellEditModalOpen(open);
  };

  const handleCollapseToggle = () => {
    dispatch(setNavigationDrawerCollapsed(!isCollapsed));
  };

  const renderGroup = (group: Group, index: number, indentLevel = 0) => {
    const isGroupActive = selectedAssetId ? false : selectedGroupAssetId === group.assetGroupName;

    return (
      <div key={group.assetGroupName + index.toString()} className='flex flex-col gap-1'>
        <div
          className={`${isGroupActive ? group.class + ' item-selected' : group.class}`}
          data-group-name={group.assetGroupName}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '6px',
              width: '100%',
              alignItems: 'center',
              paddingLeft: `${indentLevel * 14}px`,
            }}
            onClick={() => handleAssetGroupNameClick(group.id, group.assetGroupName)}
          >
            <ChevronDown
              style={{
                transform: group.showAssets ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
            <div className='navigation-content-frame-item-content'>
              <div
                data-testid={`navbar-${group.assetGroupName}`}
                className={`navigation-content-frame-item-text ${isGroupActive ? 'text-active' : ''}`}
                analytics-id={`navigation-group-name-${group.assetGroupName?.replace(/\s+/g, '-').toLowerCase() ?? 'unknown'}`}
              >
                {group.assetGroupName}
              </div>
            </div>
            <div className={`navigation-content-frame-item-count ${isGroupActive ? 'active' : ''}`}>
              {(group.childGroups?.length ?? 0) + (group.assets?.length ?? 0)}
            </div>
          </div>
          <div onClick={() => handleAssetGroupClick(group.id)}></div>
        </div>
        {group.showAssets && (
          <div>
            <div className='flex flex-col'>
              {group.childGroups?.map((childGroup, childIndex) =>
                renderGroup(childGroup, index + childIndex + 1, indentLevel + 1),
              )}
              {group.assets?.map((asset, assetIndex) => {
                const isAssetActive = selectedAssetId === asset.assetName;

                return (
                  <div
                    data-testid={`navbar-${asset.assetName}`}
                    key={asset.assetName + assetIndex.toString()}
                    className={`${isAssetActive ? 'navigation-content-frame-asset item-selected' : 'navigation-content-frame-asset'}`}
                    onClick={() => {
                      handleAssetIdClick(
                        asset.assetName,
                        group.id,
                        asset.assetId,
                        asset.industryApplicationId,
                        group.assetGroupName,
                      );
                    }}
                  >
                    <div
                      className={`navigation-content-frame-item-text ${isAssetActive ? 'text-active' : ''}`}
                      analytics-id={`navigation-asset-name-${asset.assetName?.replace(/\s+/g, '-').toLowerCase() ?? 'unknown'}`}
                      style={{
                        paddingLeft: `${(indentLevel + 1) * 20}px`,
                      }}
                    >
                      <CommStatusIcon commStatus={asset.commStatus ?? ''} commsPercent={asset.commsPercent ?? 0} />
                      <NavigationIcons
                        asset={{
                          industryApplicationId: asset.industryApplicationId,
                          runStatus: asset.runStatus,
                        }}
                        isActive={isAssetActive}
                        showStatusIndicator={true}
                      />
                      {asset.assetName}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const clearSearch = () => {
    setSearchString('');
    dispatch(
      updateSearchGroups({
        groupId: undefined,
        assetId: undefined,
        searchString: '',
        assetIdGuid: undefined,
      }),
    );
    if (searchBar.current) {
      searchBar.current.value = '';
      searchBar.current.focus();
    }
  };

  const assetGroupDetails = assetGroups
    ?.filter((group: Group) => group && group.assetGroupName)
    ?.map((value: Group, index: number) => renderGroup(value, index));

  return (
    <>
      <div style={{ position: 'relative' }}>
        <div className={`navigation-left-chevron ${isCollapsed ? 'collapsed' : ''}`} onClick={handleCollapseToggle}>
          <Tooltip content={isCollapsed ? 'Expand views panel' : 'Collapse views panel'} direction='right'>
            <ChevronIcon />
          </Tooltip>
        </div>
        <div
          data-testid='asset-navigation-drawer'
          className={`navigation-bar-items ${hasWellFilters && ' has-selected-well-filters'} ${isCollapsed ? 'collapsed' : ''}`}
        >
          <div className={`navigation-bar-items-content `}>
            <div className='navigation-content-search-and-filter'>
              <div className='navigation-content-search custom-view-search'>
                <SearchIcon className='search-icon' width='20' height='20' stroke='#647980' />
                <input
                  ref={searchBar}
                  className='input-with-icon'
                  placeholder='Search'
                  onChange={throttledHandleSearchInputChange}
                  onKeyDown={handleKeyPress}
                  tabIndex={0}
                />
                {searchString && (
                  <button className='clear-search-button' onClick={clearSearch} type='button' aria-label='Clear search'>
                    ✕
                  </button>
                )}
              </div>
              <Tooltip content='Create well group' direction='top'>
                <button className='btn-prime add-custom-view-btn' onClick={() => openAddCustomViewModal(true)}>
                  <span className='text-button btn-text-large'>Add view</span>
                  <span className='add-custom-view-btn btn-text-small'>+</span>
                </button>
              </Tooltip>
            </div>
            {assetGroups == null && <Loader />}
            <div className='navigation-content-frame'>{assetGroupDetails}</div>
          </div>
        </div>
      </div>

      <WellEditModal isOpen={isWellEditModalOpen} isAdd={true} onClose={() => setWellEditModalOpen(false)} />
    </>
  );
};

const TrackedNavigationDrawerItem = TrackingDecorator(NavigationDrawerItem as any);
export default TrackedNavigationDrawerItem;
